import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { DraggableData, DraggableEvent } from 'react-draggable'
import Draggable from 'react-draggable'
import {
  Modal as AntModal,
  Button,
  Col,
  Radio,
  RadioChangeEvent,
  Row,
  Space,
  Spin,
  Tooltip
} from 'antd'
import * as Icons from '@ant-design/icons'
import TextArea from 'antd/lib/input/TextArea'
import { useApi, useApiQuery } from 'admin/lib/Api'
import {
  KnownAddressResult,
  CurrentValuesResult,
  AddressName
} from './KnownAddressModalLink'
import { useParams } from 'react-router-dom'
import { colours } from '@qflow/theme'
import { COUNTRY, COUNTRY_ALT_NAME } from '@qflow/constants'

type FoundAddress = {
  fullAddress: string
  postCode: string
  country: string
  coordinates: {
    lat: number
    lon: number
  }
  score: number
}

type SuggestedAddresses = {
  suggestionsMatchingGiven: SuggestedAddress[]
  suggestionsGeneral: SuggestedAddress[]
}

const EMPTY_SUGGESTIONS: SuggestedAddresses = {
  suggestionsMatchingGiven: [],
  suggestionsGeneral: []
}

type SuggestedAddress = {
  fullAddress: string
  coordinates: {
    lat: number
    lon: number
  }
  count: number
}

type SuggestionState = {
  isLoading: boolean
  isExecuting: boolean
  suggestionError: string | null
  suggestions: SuggestedAddresses
}

type SearchState = {
  isSearching: boolean
  hasSearched: boolean
  searchError: string | null
  foundAddresses: FoundAddress[]
}

export interface ProjectData {
  loaded: boolean
  status: string | null
  error: string | null
  data: {
    siteAddresses: any[]
    displayName: string
  } | null
}

export type KnownAddressModalProps = {
  addressName: AddressName
  projectCountry: string
  isOpen: boolean
  getCurrentValues: () => CurrentValuesResult
  getGivenIsDirty: () => boolean
  onCancel: () => void
  onSubmit: (result: KnownAddressResult) => void
}

export function KnownAddressModal({
  addressName,
  projectCountry,
  isOpen,
  getCurrentValues,
  getGivenIsDirty,
  onCancel,
  onSubmit
}: KnownAddressModalProps) {
  const { given, known: initialKnown } = getCurrentValues()
  const [known, setKnown] = useState<string | null>(initialKnown)
  const [doSearch, setDoSearch] = useState<boolean>(false)

  const [disabled, setDisabled] = useState(true)
  const [bounds, setBounds] = useState({ left: 0, top: 0, bottom: 0, right: 0 })
  const draggleRef = useRef<HTMLDivElement>(null)

  const onStart = (_event: DraggableEvent, uiData: DraggableData) => {
    const { clientWidth, clientHeight } = window.document.documentElement
    const targetRect = draggleRef.current?.getBoundingClientRect()
    if (!targetRect) {
      return
    }
    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y)
    })
  }

  const modalTitle = useMemo(
    () => `Edit Known ${addressName} Address`,
    [addressName]
  )

  const [isSubmitting] = useState(false)

  const handleKnownChangeRequested = useCallback(() => {
    setKnown(null)
  }, [])

  const handleSetManuallyRequested = useCallback(() => {
    setDoSearch(true)
  }, [])

  const renderPanel = useCallback(() => {
    if (getGivenIsDirty()) {
      return (
        <GivenAddressHasUnsavedChangesPanel
          addressName={addressName}
          onCancel={onCancel}
        />
      )
    } else if (known && known.length > 0) {
      return (
        <KnownAddressAlreadySetPanel
          addressName={addressName}
          knownAddress={known}
          onCancel={onCancel}
          onChangeRequested={handleKnownChangeRequested}
        />
      )
    } else if (!doSearch) {
      return (
        <SuggestionAddressPanel
          addressName={addressName}
          givenAddress={given}
          onCancel={onCancel}
          onSetManuallyRequested={handleSetManuallyRequested}
          onSubmit={onSubmit}
        />
      )
    } else {
      return (
        <SearchAddressPanel
          defaultEnteredValue={given}
          projectCountry={projectCountry}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      )
    }
  }, [
    addressName,
    doSearch,
    getGivenIsDirty,
    given,
    handleKnownChangeRequested,
    handleSetManuallyRequested,
    known,
    onCancel,
    onSubmit,
    projectCountry
  ])

  return (
    <AntModal
      title={
        <div
          style={{
            width: '100%',
            cursor: 'move'
          }}
          onMouseOver={() => {
            if (disabled) {
              setDisabled(false)
            }
          }}
          onMouseOut={() => {
            setDisabled(true)
          }}
          // fix eslintjsx-a11y/mouse-events-have-key-events
          // https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/master/docs/rules/mouse-events-have-key-events.md
          onFocus={() => {}}
          onBlur={() => {}}
          // end
        >
          {modalTitle}
        </div>
      }
      open={isOpen}
      onCancel={onCancel}
      maskClosable={false}
      confirmLoading={isSubmitting}
      footer={null}
      width={600}
      wrapClassName="address-modal"
      modalRender={modal => (
        <Draggable
          disabled={disabled}
          bounds={bounds}
          onStart={(event, uiData) => onStart(event, uiData)}
        >
          <div ref={draggleRef}>{modal}</div>
        </Draggable>
      )}
      styles={{
        wrapper: { pointerEvents: 'none' }
      }}
    >
      {renderPanel()}
    </AntModal>
  )
}

function GivenAddressHasUnsavedChangesPanel({
  addressName,
  onCancel
}: {
  addressName: string
  onCancel: () => void
}) {
  return (
    <>
      <h1>Unsaved Given Address changes</h1>
      <p className="emphasise">
        This Given {addressName} Address has unsaved changes. You must save the
        record before you can set the Known Address.
      </p>
      <p>
        Saving your changes to the Given Address will automatically try to match
        the best Known address.
      </p>

      <ModalButtonRow>
        <ModalButton label="Close" onClick={onCancel} />
      </ModalButtonRow>
    </>
  )
}

function KnownAddressAlreadySetPanel({
  addressName,
  knownAddress,
  onCancel,
  onChangeRequested
}: {
  addressName: string
  knownAddress: string
  onCancel: () => void
  onChangeRequested: () => void
}) {
  return (
    <>
      <h1>Known address already set</h1>
      <p className="compress">
        This Known {addressName} Address is currently set to:
      </p>
      <span
        style={{
          display: 'block',
          margin: '0.625rem 0',
          color: 'MediumPurple'
        }}
      >
        <b>{knownAddress}</b>
      </span>

      <ModalButtonRow>
        <ModalButton label="Cancel" onClick={onCancel} />
        <ModalButton
          label="Change address"
          onClick={onChangeRequested}
          primary
        />
      </ModalButtonRow>
    </>
  )
}

function SuggestionAddressPanel({
  addressName,
  givenAddress,
  onCancel,
  onSetManuallyRequested,
  onSubmit
}: {
  addressName: AddressName
  givenAddress: string
  onCancel: () => void
  onSetManuallyRequested: () => void
  onSubmit: (result: KnownAddressResult) => void
}) {
  const [state, setState] = useState<SuggestionState>({
    isLoading: true,
    isExecuting: false,
    suggestionError: null,
    suggestions: EMPTY_SUGGESTIONS
  })
  const [selectedSuggestedAddress, setSelectedSuggestedAddress] =
    useState<SuggestedAddress | null>()

  const { accountId, projectId, moduleName, recordId } = useParams<{
    accountId: string
    projectId: string
    moduleName: string
    recordId: string
  }>()

  const project: ProjectData = useApiQuery(
    useCallback(
      api =>
        api.getProject(accountId ? accountId : '', projectId ? projectId : ''),
      [accountId, projectId]
    )
  )

  function toAddressType(value: AddressName) {
    switch (value) {
      case 'Delivery':
        return 'deliveryAddress'
      case 'Dispatch':
        return 'dispatchAddress'
      case 'Origin Site':
        return 'originSiteAddress'
      case 'Facility Site':
        return 'facilitySiteAddress'
    }
  }

  const api = useApi()

  useEffect(() => {
    if (!api) {
      console.warn('api not set')
      return
    }

    if (!state.isLoading || state.isExecuting) {
      return
    }

    setState(curr => {
      curr.isExecuting = true
      return curr
    })

    const apiGetAddressSuggestions = () =>
      api.getKnownAddressSuggestions(
        accountId ? accountId : '',
        projectId ? projectId : '',
        moduleName ? moduleName : '',
        recordId ? recordId : '',
        toAddressType(addressName)
      )

    async function getAddressSuggestions() {
      const [suggestions, , error] = await apiGetAddressSuggestions()
      if (error) {
        setState({
          isLoading: false,
          isExecuting: false,
          suggestionError: error.toString(),
          suggestions: suggestions ?? EMPTY_SUGGESTIONS
        })
      } else {
        setState({
          isLoading: false,
          isExecuting: false,
          suggestionError: null,
          suggestions: suggestions
        })
      }
    }

    getAddressSuggestions()
  }, [
    accountId,
    addressName,
    api,
    moduleName,
    projectId,
    recordId,
    state.isExecuting,
    state.isLoading
  ])

  const handleSuggestedResultSelected = useCallback(
    (e: RadioChangeEvent) => {
      const [selectedType, index] = e.target.value.split('|')
      const selectedSuggestedAddress =
        selectedType === 'MatchingGiven'
          ? state.suggestions.suggestionsMatchingGiven[index]
          : state.suggestions.suggestionsGeneral[index]
      setSelectedSuggestedAddress(selectedSuggestedAddress)
    },
    [
      state.suggestions.suggestionsGeneral,
      state.suggestions.suggestionsMatchingGiven
    ]
  )

  const handleSubmit = useCallback(() => {
    if (!selectedSuggestedAddress) {
      console.warn('Submit unexpectedly had no selected suggested address')
      return
    }
    onSubmit({
      entered: '',
      known: selectedSuggestedAddress.fullAddress,
      coordinates: selectedSuggestedAddress.coordinates
    })
  }, [onSubmit, selectedSuggestedAddress])

  const matchedSiteAddress = useCallback(
    (address: string) => {
      if (project.data?.siteAddresses) {
        const siteAddress = project.data.siteAddresses.find(
          a => a.address === address
        )
        if (!siteAddress) {
          return null
        }
        if (siteAddress) {
          return (
            <>
              <br />
              <span style={{ color: 'MediumPurple' }}>Site Address:</span>{' '}
              <span style={{ color: 'MediumPurple', fontWeight: '600' }}>
                {siteAddress.displayName}
              </span>
            </>
          )
        }
      }
      return null
    },
    [project]
  )

  const renderSuggestedAddresses = useCallback(
    (
      resultType: 'MatchingGiven' | 'General',
      addresses: SuggestedAddress[]
    ) => (
      <Space direction="vertical">
        {addresses.map((a, i) => (
          <Radio key={i} value={`${resultType}|${i}`}>
            <span className="emphasise">{a.fullAddress}</span>{' '}
            <span className="subscript">
              ({a.count}
              {i === 0 && ` usage${a.count === 1 ? '' : 's'}`})
            </span>
            {matchedSiteAddress(a.fullAddress)}
          </Radio>
        ))}
      </Space>
    ),
    [matchedSiteAddress]
  )

  return (
    <>
      <h1>Option 1: Choose from previous records</h1>
      <p>
        Choosing values from previous records ensures consistency, so unless
        this is a new Given Address then we should be able to pick the
        corresponding value from a previous record.
      </p>
      {state.isLoading && (
        <Row>
          <Col offset={10} span={4}>
            <Spin />
          </Col>
        </Row>
      )}

      {!state.isLoading && state.suggestionError && (
        <p className="error">
          Failed to retrieve suggested addresses: {state.suggestionError}
        </p>
      )}

      {!state.isLoading && !state.suggestionError && (
        <Radio.Group onChange={handleSuggestedResultSelected}>
          <p className="radio-group-divider">
            Known {addressName} addresses on records that have a given address
            of:
            <span
              style={{
                display: 'block',
                margin: '0.625rem 0',
                color: 'deeppink'
              }}
            >
              <b>{givenAddress}</b>
            </span>
          </p>

          {state.suggestions.suggestionsMatchingGiven.length > 0 &&
            renderSuggestedAddresses(
              'MatchingGiven',
              state.suggestions.suggestionsMatchingGiven
            )}
          {state.suggestions.suggestionsMatchingGiven.length === 0 && (
            <p className="warning">
              There are no records which have the same given address.
            </p>
          )}

          <p className="radio-group-divider">
            Known {addressName} addresses on all records in the project:
          </p>
          {state.suggestions.suggestionsGeneral.length > 0 &&
            renderSuggestedAddresses(
              'General',
              state.suggestions.suggestionsGeneral
            )}
          {state.suggestions.suggestionsGeneral.length === 0 && (
            <p className="warning">
              There are no records with known addresses in this project yet. You
              will need to set the address manually.
            </p>
          )}
        </Radio.Group>
      )}

      <ModalButtonRow>
        <ModalButton label="Cancel" onClick={onCancel} />
        <ModalButton label="Set manually" onClick={onSetManuallyRequested} />
        <ModalButton
          label="Update and close"
          onClick={handleSubmit}
          primary
          disabled={state.isLoading || !selectedSuggestedAddress}
        />
      </ModalButtonRow>
    </>
  )
}

function SearchAddressPanel({
  defaultEnteredValue,
  projectCountry,
  onCancel,
  onSubmit
}: {
  defaultEnteredValue: string
  projectCountry: string
  onCancel: () => void
  onSubmit: (result: KnownAddressResult) => void
}) {
  const [enteredValue, setEnteredValue] = useState<string>(defaultEnteredValue)
  const [searchState, setSearchState] = useState<SearchState>({
    isSearching: false,
    hasSearched: false,
    searchError: null,
    foundAddresses: []
  })
  const [selectedFoundAddress, setSelectedFoundAddress] =
    useState<FoundAddress | null>()

  const api = useApi()

  const { projectId, accountId } = useParams<{
    projectId: string
    accountId: string
  }>()

  const handleSearching = useCallback(() => {
    if (!api) {
      console.warn('api not set')
      return
    }
    const apiGetGeocoding = () =>
      api.getAddresses(
        enteredValue,
        accountId ? accountId : '',
        projectId ? projectId : ''
      )

    setSearchState({
      isSearching: true,
      hasSearched: false,
      searchError: null,
      foundAddresses: []
    })

    async function searchAddresses() {
      const [foundAddresses, , error] = await apiGetGeocoding()
      if (error) {
        setSearchState({
          isSearching: false,
          hasSearched: true,
          searchError: error.toString(),
          foundAddresses: foundAddresses
        })
      } else {
        setSearchState({
          isSearching: false,
          hasSearched: true,
          searchError: null,
          foundAddresses: foundAddresses
        })
      }
    }

    searchAddresses()
  }, [api, enteredValue, projectId, accountId])

  const handleMatchedResultSelected = useCallback(
    e => {
      const selectedMatchedAddress = searchState.foundAddresses[e.target.value]
      setSelectedFoundAddress(selectedMatchedAddress)
    },
    [searchState.foundAddresses]
  )

  const handleSubmit = useCallback(() => {
    if (!selectedFoundAddress) {
      console.warn('Submit unexpectedly had no selected matched address')
      return
    }
    onSubmit({
      entered: enteredValue,
      known: selectedFoundAddress.fullAddress,
      coordinates: selectedFoundAddress.coordinates
    })
  }, [enteredValue, onSubmit, selectedFoundAddress])

  return (
    <>
      <h1>Option 2: Search for the address</h1>
      <p>
        Please locate the address by searching for it. More specific inputs will
        be more accurate. Post/Zip codes can be entered.
      </p>

      <details open>
        <summary className="emphasise">
          Better results can be found by...
        </summary>
        <ul>
          <li>Removing units e.g. Unit 5</li>
          <li>Removing block numbers e.g. Block J1</li>
          <li>Removing supplier/contractor</li>
        </ul>
      </details>

      <p className="compress">
        <label className="input-label" htmlFor="addressSearchInput">
          Search address
        </label>
      </p>

      <Row gutter={3} style={{ marginBottom: '10px' }}>
        <Col flex="auto">
          <TextArea
            id="addressSearchInput"
            autoSize
            rows={1}
            defaultValue={defaultEnteredValue}
            onChange={e => setEnteredValue(e.target.value)}
          />
        </Col>
        <Col>
          <Button
            tabIndex={-1}
            icon={<Icons.SearchOutlined />}
            onClick={() => handleSearching()}
            aria-label="Search"
          />
        </Col>
      </Row>

      {searchState.isSearching && (
        <Row>
          <Col offset={10} span={4}>
            <Spin />
          </Col>
        </Row>
      )}

      {!searchState.isSearching && searchState.searchError && (
        <p className="error">
          Failed to search addresses: {searchState.searchError}
        </p>
      )}

      {!searchState.isSearching &&
        !searchState.searchError &&
        searchState.hasSearched &&
        searchState.foundAddresses.length === 0 && (
          <p className="warning">No addresses found matching search text</p>
        )}

      {!searchState.isSearching &&
        !searchState.searchError &&
        searchState.foundAddresses.length > 0 && (
          <Radio.Group onChange={handleMatchedResultSelected}>
            <Space direction="vertical">
              {searchState.foundAddresses.map((a, i) => (
                <Radio key={i} value={i}>
                  <AddressSearchResultDisplay
                    foundAddress={a}
                    projectCountry={projectCountry}
                  />
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        )}

      <ModalButtonRow>
        <ModalButton label="Cancel" onClick={onCancel} />
        <ModalButton
          label="Update and close"
          onClick={handleSubmit}
          primary
          disabled={!selectedFoundAddress}
        />
      </ModalButtonRow>
    </>
  )
}

function AddressSearchResultDisplay({
  foundAddress,
  projectCountry
}: {
  foundAddress: FoundAddress
  projectCountry: string
}) {
  const alertIfCountryDifferentToProject = useCallback(
    (resultCountry: string) => {
      // get key from COUNTRY where value matches projectCountry
      const projectCountryKey = Object.keys(COUNTRY).find(
        key => COUNTRY[key as keyof typeof COUNTRY] === projectCountry
      )

      if (
        resultCountry?.toLowerCase() === projectCountry?.toLowerCase() ||
        COUNTRY_ALT_NAME[projectCountryKey || '']?.some(
          (name: string) => name.toLowerCase() === resultCountry.toLowerCase()
        )
      ) {
        return null
      }

      return (
        <Tooltip
          title={`Country '${resultCountry}' is different from project country '${projectCountry}'`}
        >
          <Icons.WarningOutlined
            style={{ color: colours.ERROR, marginLeft: '5px' }}
            title="Warning: Different Country"
          />
        </Tooltip>
      )
    },
    [projectCountry]
  )
  return (
    <>
      <Tooltip
        title={`${foundAddress.country} [Score: ${Math.round(foundAddress.score * 1000) / 1000}]`}
      >
        <span className="emphasise">{foundAddress.fullAddress}</span>
      </Tooltip>
      {alertIfCountryDifferentToProject(foundAddress.country)}
    </>
  )
}

function ModalButtonRow({ children }: { children: React.ReactNode }) {
  return (
    <Row className="modal-buttons">
      <Col flex="auto"></Col>
      <Col>{children}</Col>
    </Row>
  )
}

function ModalButton({
  label,
  primary,
  disabled,
  onClick
}: {
  label: string
  primary?: boolean
  disabled?: boolean
  onClick: () => void
}) {
  const type = primary ? 'primary' : 'default'
  const htmlType = primary ? 'submit' : undefined
  const style = primary ? undefined : { marginRight: '10px' }
  return (
    <Button
      type={type}
      htmlType={htmlType}
      disabled={disabled}
      aria-label={label}
      style={style}
      onClick={onClick}
    >
      {label}
    </Button>
  )
}
